import { parseToRgb, rgba, rgbToColorString } from 'polished';
import { RgbaColor as PolishedRgba, RgbColor as PolishedRgb } from 'polished/lib/types/color';
import { RGBA as ColorBlendRgba } from 'color-blend/dist/types';
import { normal } from 'color-blend';
import { GlobalTheme } from '@cld/component-theme';
import { backgroundMapping, foregroundMapping } from './mapping';
import { BlenderBackgroundProps, BlenderForegroundProps, BlendMode, BlendVariant } from './BlenderProps';
import { BackgroundBlendOptions, ColorName, OpaqueColorName } from './blendOptions';

const toColorBlendRgba = (color: PolishedRgba | PolishedRgb): ColorBlendRgba => ({
  r: color.red,
  g: color.green,
  b: color.blue,
  a: 'alpha' in color ? color.alpha : 1,
});

const fromBlendToPolishedRgba = (color: ColorBlendRgba): PolishedRgba => ({
  red: color.r,
  green: color.g,
  blue: color.b,
  alpha: color.a,
});

const toPolishedRgba = (color: string): PolishedRgba | PolishedRgb =>
  color === 'transparent' ? { red: 255, green: 255, blue: 255, alpha: 0 } : parseToRgb(color);

const resolveColor = (color: ColorName, colors: Record<OpaqueColorName, string>) => (color === 'transparent' ? 'transparent' : colors[color]);

const computeBlendColor = (blending: BackgroundBlendOptions, colors: Record<Exclude<ColorName, 'transparent'>, string>) => {
  const baseColor = resolveColor(blending.baseColor, colors);
  const blendColor = rgba(resolveColor(blending.blendColor, colors), blending.blendOpacity);
  const blendBackdrop = toColorBlendRgba(toPolishedRgba(baseColor));
  const blendSource = toColorBlendRgba(toPolishedRgba(blendColor));

  return rgbToColorString(fromBlendToPolishedRgba(normal(blendBackdrop, blendSource)));
};

export const blenderBackground = (props: BlenderBackgroundProps & { theme: GlobalTheme }) =>
  computeBlendColor(backgroundMapping[props.mode][props.variant][props.intensity], props.theme.palette);

export const blenderForeground = ({ mode, variant, theme }: BlenderForegroundProps & { theme: GlobalTheme }) =>
  resolveColor(foregroundMapping[mode][variant].colorName, theme.palette);

export const getBlenderTypographyType = (mode: BlendMode, variant: BlendVariant) => foregroundMapping[mode][variant].typography;
